import React from 'react';

import { type LucideIcon, XIcon } from 'lucide-react';
import { type VariantProps, tv } from 'tailwind-variants';

import { cn } from 'common/style/utils';
import Button from 'common/uiV3/Button/Button';
import ButtonLink from 'common/uiV3/Button/ButtonLink';
import Flex from 'common/uiV3/Flex';

export const noticeBarVariants = tv({
  slots: {
    base: 'relative items-center justify-center py-2 bg-indigo-120 text-white',
    content: 'flex-wrap items-center justify-center gap-2.5 px-6 [&>svg]:size-4',
    message: 'text-center',
    action: '',
    dismissButton: 'absolute right-2 opacity-50 hover:opacity-100',
  },
  variants: {
    dismissible: {
      true: '',
    },
    variant: {
      default: {
        base: 'bg-indigo-120 dark:bg-indigo-40',
        action: 'text-white dark:text-foreground! hover:text-white dark:hover:text-foreground!',
        message: 'text-white dark:text-foreground! hover:text-white dark:hover:text-foreground!',
      },
      neutral: {
        base: 'bg-foreground',
        action: 'text-white! hover:text-white!',
        message: 'text-white!',
      },
    },
  },
  defaultVariants: {
    variant: 'default',
    dismissible: false,
  },
});

type ButtonAction = {
  type: 'button';
  label: string;
  onClick: () => void;
};

type ExternalLinkAction = {
  type: 'link';
  isExternal: true;
  label: string;
  href: string;
  to?: never;
};

type InternalLinkAction = {
  type: 'link';
  isExternal?: false;
  label: string;
  to: string;
  href?: never;
};

type LinkAction = ExternalLinkAction | InternalLinkAction;

type Action = ButtonAction | LinkAction;

type NoticeBarProps = VariantProps<typeof noticeBarVariants> & {
  icon?: LucideIcon;
  message: React.ReactNode;
  action: Action;
  dismissible?: boolean;
  onDismiss?: () => void;
};

const NoticeBar = ({
  icon: Icon,
  message,
  action,
  variant,
  dismissible = false,
  onDismiss,
}: NoticeBarProps) => {
  const styles = noticeBarVariants({
    variant,
    dismissible,
  });

  return (
    <Flex className={styles.base()}>
      <Flex className={styles.content()}>
        {Icon && <Icon />}
        <p className={styles.message()}>{message}</p>
        {action.type === 'button' ? (
          <Button onClick={action.onClick} variant="link" className={styles.action()}>
            {action.label}
          </Button>
        ) : action.isExternal ? (
          <ButtonLink href={action.href} variant="link" className={styles.action()}>
            {action.label}
          </ButtonLink>
        ) : (
          <ButtonLink to={action.to} variant="link" className={styles.action()}>
            {action.label}
          </ButtonLink>
        )}
      </Flex>
      {dismissible && (
        <Button
          onClick={onDismiss}
          variant="link"
          className={cn(styles.action(), styles.dismissButton())}>
          <XIcon />
        </Button>
      )}
    </Flex>
  );
};

export default NoticeBar;
